import { FormError, FormValidators } from "../hooks/useForm";

export const requiredFieldValidator =
    (errorMessage: string) =>
    <T>(field: keyof T) =>
        Promise.resolve(
            field == undefined ||
                field == null ||
                (typeof field === "string" && field === "") ||
                (typeof field === "boolean" && field === false) ||
                (Array.isArray(field) && field.length === 0)
                ? errorMessage
                : undefined
        );

export interface ItemPropertyError {
    property: string;
    error: FormError | FormError[];
    rowIndex: number;
}
export interface ArrayItemError {
    properties: ItemPropertyError[];
    errorIndex: number;
}
export const isArrayItemError = (error: object): error is ArrayItemError =>
    "properties" in error && "index" in error;
export const arrayValidator =
    <T, U>(validators: FormValidators<U>) =>
    (field: keyof T): Promise<FormError[]> =>
        new Promise(async resolve => {
            if (!Array.isArray(field)) throw Error("You need to provide an array");

            if (!validators) {
                resolve([]);
                return;
            }

            const errors: Array<ArrayItemError> = [];
            let errorIndex = 0,
                rowIndex = 0;
            const propertiesWithValidators = Object.keys(validators) as (keyof U)[];
            for (const property of propertiesWithValidators) {
                const propertyName = String(property);
                const validatorList = validators[property];
                if (!validatorList)
                    throw Error(`Failed to get validator list for property ${propertyName}`);

                const itemError: ArrayItemError = { properties: [], errorIndex };
                rowIndex = 0;
                for (const item of field) {
                    const valueToValidate = item[property] as keyof U;
                    for (const validator of validatorList) {
                        const error = await validator(valueToValidate, item);
                        if (error) {
                            itemError.properties.push({
                                property: propertyName,
                                error,
                                rowIndex,
                            });
                            break;
                        }
                    }
                    rowIndex++;
                }
                if (itemError.properties.length) errors.push(itemError);

                errorIndex++;
            }

            resolve(errors);
        });

interface NumberRequirements {
    minValue?: number;
    maxValue?: number;
    minExcluded?: boolean;
    maxExcluded?: boolean;
}

export const int64Requirements: NumberRequirements = {
    minValue: -9223372036854775808.0,
    minExcluded: false,
    maxValue: 9223372036854775807.0,
    maxExcluded: false,
};

export const greaterThanZeroRequirements: NumberRequirements = {
    minValue: 0,
    minExcluded: false,
};

export const greaterThanOrEqualToZeroRequirements: NumberRequirements = {
    minValue: 0,
    minExcluded: true,
};

export const numberFieldValidator =
    (requirements?: NumberRequirements) =>
    <T>(field: Extract<keyof T, string>): Promise<string | undefined> =>
        new Promise(resolve => {
            if (!requirements) {
                resolve(undefined);
                return;
            }

            let message: string | undefined = undefined;

            const { minValue, minExcluded, maxValue, maxExcluded } = requirements;

            if (field !== undefined && field !== null) {
                const parsed = parseFloat(field);
                if (isNaN(parsed)) {
                    message = "The provided value is not a number";
                } else {
                    if (minValue !== undefined) {
                        if (minExcluded) {
                            if (parsed < minValue)
                                message = `Value can't be lower than ${minValue}`;
                        } else {
                            if (parsed <= minValue)
                                message = `Value can't be lower than or equal to ${minValue}`;
                        }
                    }
                    if (maxValue !== undefined && message == undefined) {
                        if (maxExcluded) {
                            if (parsed > maxValue)
                                message = `Value can't be greater than ${minValue}`;
                        } else {
                            if (parsed >= maxValue)
                                message = `Value can't be greater than or equal to ${minValue}`;
                        }
                    }
                }
            }
            resolve(message);
        });

const emailRegex = new RegExp("^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,6}$");
export const emailFieldValidator =
    () =>
    <T>(field: Extract<keyof T, string>) =>
        Promise.resolve(
            field.length && !emailRegex.test(field) ? "Email nije u dobrom formatu" : undefined
        );

export const oibFieldValidator =
    () =>
    <T>(field: Extract<keyof T, string>) =>
        Promise.resolve(
            field.length && !isValidOib(field) ? "OIB nije u dobrom formatu" : undefined
        );

export const isValidOib = (oib: string) => {
    if (oib.length !== 11 || oib.match(/\d{11}/) == null) return false;

    let calculated = 10;

    for (const digit of oib.substring(0, 10)) {
        calculated += parseInt(digit);
        calculated %= 10;

        if (calculated === 0) calculated = 10;

        calculated *= 2;
        calculated %= 11;
    }

    let check = 11 - calculated;
    if (check === 10) check = 0;

    return check === parseInt(oib[10]);
};

export const stringLengthValidator =
    (limit: number) =>
    <T>(field: Extract<keyof T, string>) =>
        Promise.resolve(
            field.length && field.length > limit
                ? `Character limit reached (maximum: ${limit})`
                : undefined
        );
